<template>
  <article
    class="banner"
    v-if="banner"
  >
    <div class="container banner__wrapper">
      <div class="banner__content">
        <app-content
          class="banner__description margin__b--30"
          :content="translate(banner.content)"
        />

        <ul
          class="social"
          v-if="socialNetworks.length > 0"
        >
          <li
            class="social__li"
            v-for="({ url, network }, i) in socialNetworks"
            :key="'banner_social_network_' + i"
          >
            <a
              class="social__link"
              :href="translate(url)"
              target="_blank"
              v-if="translate(url)"
            >
              <fa-icon
                size="sm"
                :icon="['fab', network]"
                class="social__icon"
              />
            </a>
          </li>
        </ul>
      </div>

      <figure class="banner__figure">
        <img
          :src="translate(banner.image.src)"
          :alt="translate(banner.image.alt)"
          class="banner__image"
          v-if="translate(banner.image.src)"
        >

        <figcaption
          class="banner__figcaption"
          v-if="translate(banner.image.alt)"
        >
          {{ translate(banner.image.alt) }}
        </figcaption>
      </figure>
    </div>
  </article>

  <section
    class="building bg__color--white"
    v-if="building && rooms.length > 0"
  >
    <div class="container building__container">
      <h2
        class="title__h2 bold color--lightblue align--center"
        v-if="translate(building.title)"
      >
        {{ translate(building.title) }}
      </h2>

      <crowdfunding-ilot-map
        :campaign="campaignId"
        :rooms="rooms"
        class="building__wrapper margin__t--48"
      />

      <ul class="legend building__legend">
        <li
          class="legend__item legend__item--funded"
          v-if="translate(building.legend.already_funded)"
        >
          {{ translate(building.legend.already_funded) }}
        </li>

        <li
          class="legend__item"
          v-if="translate(building.legend.to_funded)"
        >
          {{ translate(building.legend.to_funded) }}
        </li>
      </ul>
    </div>
  </section>

  <section
    class="campaign"
    v-if="campaign"
  >
    <div class="container campaign__container">
      <div class="campaign__thermometer">
        <crowdfunding-ilot-thermometer
          class="campaign__illu"
          :goal="campaign.goal"
          :metric="campaign.metric"
        />
      </div>

      <div class="campaign__content">
        <h2
          class="title__h2 color--darkblue bold"
          v-if="translate(campaign.title)"
        >
          {{ translate(campaign.title) }}
        </h2>

        <app-content
          class="campaign__description margin__t--24"
          :content="translate(campaign.content)"
        />

        <ul
          v-if="campaign.cta.length > 0"
          class="margin__t--24 campaign__cta"
        >
          <li
            v-for="({ link, color, type }, key) in campaign.cta"
            :key="`campaign_cta_${key}`"
          >
            <app-link
              :link="link"
              :class="`btn btn__${type}--${color} d-block`"
            />
          </li>
        </ul>
      </div>
    </div>
  </section>

  <section
    class="projects"
    v-if="allProjects.length"
  >
    <div class="container">
      <h2
        class="title__h2 color--lightblue align--center bold"
        v-if="translate(projects.title)"
      >
        {{ translate(projects.title) }}
      </h2>

      <div class="margin__t--48 row">
        <section
          class="col-12 col-sm-6 col-md-3"
          v-for="project in allProjects"
          :key="project.id"
        >
          <project-card
            class="project-card"
            :project="project"
          />
        </section>
      </div>

      <div
        class="align--center margin__t--48"
        v-if="projects.cta && projects.cta.link"
      >
        <app-link
          :link="projects.cta.link"
          :class="`btn btn__${projects.cta.type}--${projects.cta.color} d-inline-block`"
        />
      </div>
    </div>
  </section>

  <section
    class="howitworks"
    v-if="steps.length"
  >
    <div class="container">
      <h2
        class="title__h2 color--lightblue align--center bold"
        v-if="translate(howitworks.title)"
      >
        {{ translate(howitworks.title) }}
      </h2>

      <ul class="steps margin__t--48">
        <li
          class="steps__li"
          v-for="(step, key) in steps"
          :key="`step_${key}`"
        >
          <img
            :src="translate(step.icon)"
            :alt="translate(step.title)"
            class="steps__icon"
          >

          <h3
            class="title__h3 color--darkblue bold"
            v-if="translate(step.title)"
          >
            {{ translate(step.title) }}
          </h3>

          <app-content
            class="margin__t--6"
            :content="translate(step.content)"
          />

          <app-link
            :link="step.link"
            class="link__color--sec d-inline-block bold margin__t--12 steps__cta"
          />
        </li>
      </ul>
    </div>
  </section>

  <section
    class="testimonials"
    v-if="testimonials && translate(testimonials.video)"
  >
    <div class="container">
      <h2
        class="title__h2 color--lightblue align--center bold"
        v-if="translate(testimonials.title)"
      >
        {{ translate(testimonials.title) }}
      </h2>

      <app-video-embed
        :src="$i18n.translate(testimonials.video)"
        class="margin__t--48"
      />

      <div
        class="align--center margin__t--60"
        v-if="testimonials.cta && testimonials.cta.link"
      >
        <app-link
          :link="testimonials.cta.link"
          :class="`btn btn__${testimonials.cta.type}--${testimonials.cta.color} d-inline-block`"
        />
      </div>
    </div>
  </section>

  <article
    class="about"
    v-if="about"
  >
    <div class="container">
      <h2
        class="title__h2 color--lightblue align--center bold"
        v-if="translate(about.title)"
      >
        {{ translate(about.title) }}
      </h2>
    </div>

    <div class="container about__container margin__t--60">
      <figure
        class="about__figure"
        :class="{ 'bg__color--prim': !translate(about.image) }"
      >
        <img
          :src="translate(about.image)"
          :alt="translate(about.title)"
          class="about__image"
          v-if="translate(about.image)"
        >
        <fa-icon
          :icon="['far', 'image']"
          class="no-thumb"
          v-else
        />
      </figure>

      <div class="about__content">
        <app-content
          class="about__description"
          :content="translate(about.content)"
        />

        <app-link
          v-if="about.cta"
          :link="about.cta.link"
          :class="`btn btn__${about.cta.type}--${about.cta.color} margin__t--36 d-inline-block`"
        />
      </div>
    </div>
  </article>

  <section
    class="partners"
    v-if="sponsors.length"
  >
    <div class="container">
      <h2
        class="title__h2 color--lightblue align--center bold"
        v-if="translate(partners.title)"
      >
        {{ translate(partners.title) }}
      </h2>

      <ul class="sponsors">
        <li
          class="sponsors__li"
          v-for="({ logo, name, url }, key) in sponsors"
          :key="`sponsor_${key}`"
        >
          <a
            :href="translate(url)"
            target="_blank"
            class="d-block"
            v-if="translate(url)"
          >
            <img
              :src="translate(logo)"
              :alt="translate(name)"
              class="sponsors__logo"
            >
          </a>

          <img
            :src="translate(logo)"
            :alt="translate(name)"
            class="sponsors__logo"
            v-else
          >
        </li>
      </ul>
    </div>
  </section>
</template>

<script setup>
import { defineAsyncComponent } from 'vue'
import { useProjectStore } from '@/stores/ProjectStore'
import { useI18n } from '@/vendors/i18n'
import { get as _get } from 'lodash'
import { useBranding } from '@/vendors/branding'

const AppLink = defineAsyncComponent(() => import('&/atoms/AppLink'))
const AppContent = defineAsyncComponent(() => import('&/atoms/AppContent'))
const AppVideoEmbed = defineAsyncComponent(() => import('&/atoms/AppVideoEmbed'))
const ProjectCard = defineAsyncComponent(() => import('&/organisms/ProjectCard'))
const CrowdfundingIlotMap = defineAsyncComponent(() => import('./CrowdfundingIlotMap'))
const CrowdfundingIlotThermometer = defineAsyncComponent(() => import('./CrowdfundingIlotThermometer'))

const { sections } = defineProps({
  sections: { type: Object, required: true }
})

const { translate } = useI18n()
const { loadFonts } = useBranding()
const { getProjects } = useProjectStore()

const { banner, building, campaign, projects, howitworks, testimonials, about, partners } = sections.attributes

const rooms = _get(building, 'rooms', [])
const campaignId = _get(building, 'campaign_id', [])
const steps = _get(howitworks, 'steps', [])
const sponsors = _get(partners, 'sponsors', []).filter(sponsor => sponsor.logo && translate(sponsor.logo))
const socialNetworks = _get(banner, 'social_networks', []).filter(social => translate(social.url))

const { projects: allProjects } = getProjects({ query: { per_page: 4, sort_by: 'activity_last', ..._get(projects, 'query', {}) }, dependencies: [projects] })

loadFonts({ google: { families: ['Indie Flower'] } })
</script>

<style lang="scss" scoped>
$font: 'Indie Flower', cursive;
$lightblue: #0D7EA3;
$darkblue: #35364E;

.color {
  &--lightblue {
    color: $lightblue;
  }

  &--darkblue {
    color: $darkblue;
  }
}

:deep(.btn) {
  display: inline-block;
  border-radius: 30px;
  border-width: 2px;
  padding: calc($padding__base - 2px) $padding__base * 1.5;
  font-size: rem(18px);
  text-align: center;

  @include mq(md) {
    font-size: rem(16px);
  }

  @include mq(lg) {
    padding: calc($padding__base - 2px) $padding__base * 2.5;
  }

  @include mq(xl) {
    font-size: rem(18px);
  }

  &[class*='btn__solid'] {
    border-width: 2px;
    padding-top: calc($padding__base - 4px);
    padding-bottom: calc($padding__base - 4px);
  }
}
.title {
  &__h2 {
    font-size: rem(48px);
    line-height: 0.9;
  }

  &__h3 {
    font-size: rem(24px);
  }
}

.no-thumb {
  color: $white;
  font-size: rem(72px);
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}

.banner {
  background: #FFFAF5;
  padding: $padding__base * 2.5 0;

  @include mq(md) {
    padding: $padding__base * 3.5 0 $padding__base * 2.5;
  }

  &__wrapper {
    display: grid;
    grid-template-columns: 1fr;

    @include mq(md) {
      grid-template-columns: repeat(12, 1fr);
    }
  }

  &__content {
    padding-bottom: $padding__base * 2.5;

    @include mq(md) {
      grid-row: 1;
      grid-column: 1/7;
      padding: $padding__base * 4 0 $padding__base * 6;
    }

    @include mq(lg) {
      grid-column: 2/7;
    }
  }

  &__figure {
    position: relative;

    @include mq(md) {
      display: flex;
      align-items: center;
      justify-content: center;
      grid-column: 8/13;
      grid-row: 1;
      min-height: 100%;
    }
  }

  &__description {
    font-size: rem(24px);

    &:deep() {
      h1 {
        font-size: rem(24px);
      }

      u {
        text-decoration: none;
        font-weight: 700;
        color: $color--primary;
      }

      strong {
        color: $color--secondary;
      }
    }
  }

  &__image {
    display: block;
    width: 100%;
    height: auto;
    max-width: 530px;
    margin: 0 auto;
  }

  &__figcaption {
    font-family: $font;
    max-width: 362px;
    color: #35364E;
    font-size: rem(20px);
    line-height: 1;

    @include mq(md) {
      text-align: right;
      position: absolute;
      left: 0;
      bottom: -12px;
      transform: translateX(-100%);
    }

    @include mq(lg) {
      bottom: 35px;
    }

    &::before {
      content: '';
      display: block;
      width: 100%;
      height: 27px;
      margin-bottom: $margin__base;
      background-image: url(data:image/svg+xml;base64,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);
      background-position: center left;
      background-repeat: no-repeat;
      background-size: auto 100%;

      @include mq(md) {
        background-position: center right;
      }
    }
  }
}

.social {
  display: flex;
  align-items: center;

  &__li {
    &:not(:last-child) {
      margin-right: $margin__base;
    }
  }

  &__link {
    color: #131415;
    display: block;
    position: relative;
    text-align: center;
    width: 35px;
    height: 35px;
    border: 1px solid #131415;
    border-radius: 50%;
    transition: all $transition__duration;

    &:hover {
      color: $color--secondary;
    }

    & > * {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
    }
  }
}

.building {
  padding: $padding__base * 5 0;
  position: relative;

  @include mq(md) {
    background: url('https://koalect-images.s3.eu-west-3.amazonaws.com/clients/ilot/building_illu_top.svg');
    background-position: top -30px left -60px;
    background-repeat: no-repeat;
    background-size: 324px auto;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      background: url('https://koalect-images.s3.eu-west-3.amazonaws.com/clients/ilot/building_illu_bottom.svg');
      background-position: bottom 60px right -52px;
      background-repeat: no-repeat;
      background-size: 296px auto;
    }
  }

  &__container {
    position: relative;
    z-index: 10;
  }

  &__wrapper {
    max-width: 696px;
    overflow-x: auto;
    margin-left: auto;
    margin-right: auto;

    -webkit-overflow-scrolling: touch;
  }

  &__legend {
    margin-top: $margin__base * 2;
  }
}

.legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;

  @include mq(md) {
    display: block;
    position: absolute;
    bottom: 0;

    @include mq(lg) {
      right: 0;
      bottom: 160px;
    }
  }

  &__item {
    display: flex;
    align-items: center;
    font-size: rem(18px);
    padding: $padding__base;

    @include mq(md) {
      font-size: rem(24px);
      padding: 0;
    }

    &:not(:last-child) {
      @include mq(md) {
        margin-bottom: $margin__base * 1.5;
      }
    }

    &::before {
      content: '';
      display: block;
      width: 24px;
      height: 24px;
      border: 2px solid #4D7381;
      background: #FFFAF5;
      margin-right: $margin__base;
      border-radius: 50%;
    }

    &--funded::before {
      background: #5DB783;
    }
  }
}

.campaign {
  background: #BDD7E0;
  position: relative;
  z-index: 10;

  &__container {
    display: grid;
    grid-template-columns: 1fr;
    align-items: center;

    @include mq(md) {
      grid-template-columns: repeat(12, 1fr);
    }
  }

  &__thermometer {
    @include mq(md) {
      grid-row: 1;
      grid-column: 1/6;
    }

    @include mq(xl) {
      grid-column: 1/5;
    }
  }

  &__content {
    padding: $padding__base * 5 0;

    @include mq(md) {
      padding: $padding__base * 10 0;
      grid-row: 1;
      grid-column: 7/13;
    }
  }

  &__illu {
    max-width: 100%;
    height: auto;
    margin: $margin__base * -2.5 auto 0;

    @include mq(md) {
      margin: 0 auto;
    }

    @include mq(lg) {
      max-width: 461px;
      margin: $margin__base * -5 auto 0;
    }
  }

  &__description {
    font-size: rem(18px);
  }

  &__cta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;

    li {
      margin: $margin__base $margin__base 0 0;

      &:last-child {
        margin-right: 0;
      }
    }
  }
}

.projects {
  padding: $padding__base * 5 0;
  background: $white;

  @include mq(md) {
    padding: $padding__base * 5 0 $padding__base * 10;
  }
}

.howitworks {
  padding: $padding__base * 5 0;
  background: #FFF3E9;

  @include mq(md) {
    padding: $padding__base * 5 0 $padding__base * 10;
  }
}

.steps {
  display: grid;
  grid-template-columns: 1fr;
  gap: $margin__base * 4;
  text-align: center;

  @include mq(sm) {
    grid-template-columns: repeat(2, 1fr);
    text-align: left;
    gap: $margin__base * 2;
  }

  @include mq(md) {
    grid-template-columns: repeat(3, 1fr);
  }

  &__icon {
    height: 68px;
    width: auto;
    max-width: 100%;
    margin-bottom: 3px;
  }

  &:deep(.steps__cta) {
    font-size: rem(24px);
  }
}

.testimonials {
  padding: $padding__base * 5 0;
  background: $white;

  & + .about,
  & + .partners {
    padding-top: 0;
  }
}

.about {
  background: $white;
  padding: $padding__base * 5 0;

  & + .partners {
    padding-top: 0;
  }

  &__container {
    display: grid;
    grid-template-columns: 1fr;
    align-items: flex-start;

    @include mq(md) {
      grid-template-columns: repeat(12, 1fr);
    }
  }

  &__figure {
    position: relative;

    @include aspect-ratio(650, 557);

    @include mq(md) {
      grid-column: 1/7;
      grid-row: 1;
    }
  }

  &__image {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: auto;
  }

  &__content {
    padding: $padding__base * 2 0;

    @include mq(md) {
      grid-column: 8/13;
      grid-row: 1;
      padding: $padding__base * 4 0;
    }
  }

  &__description {
    font-size: rem(18px);

    &:deep() {
      u {
        text-decoration: none;
        font-weight: 700;
        color: $color--primary;
      }

      strong {
        color: $color--secondary;
      }
    }
  }
}

.partners {
  background: $white;
  padding: $padding__base * 5 0;

  @include mq(md) {
    padding-bottom: $padding__base * 10;
    background-image: url('https://koalect-images.s3.eu-west-3.amazonaws.com/clients/ilot/building_illu_top.svg');
    background-position: bottom -10px right -108px;
    background-repeat: no-repeat;
    background-size: 363px auto;
  }
}

.sponsors {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  max-width: 710px;
  margin: $margin__base * 3 auto 0;

  &__li {
    padding: $padding__base;
  }

  &__logo {
    max-height: 98px;
    width: auto;
    display: block;
  }
}
</style>